// src/oura/screens/OnboardingTab.tsx
//
// One client's onboarding, for staff: the one thing to do next at the top,
// the steps under it, and what we hold beside them.
//
// The screen is built so that nobody has to work out what to do. The Next
// card names it and carries the button. Everything else is the record.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BeakerIcon, CheckIcon, KeyRoundIcon, Loader2Icon, ListChecksIcon, UserIcon } from "lucide-react";
import { cn } from "../lib/cn";
import {
    Bento, BentoStack, FOCUS_RING, FoldCard, FoldScope, ListRow, PAGE_GROUND, PageSentence, PillButton,
    Surface, TYPE, type Tone,
} from "@oura/ui";
import { getEngagementIfExists } from "@service/aeo/clientReview";
import { KEYWORDS_FIELD_ID, KEYWORDS_MIN, keywordsOf } from "../components/plan/planModel";
import { listBusinesses } from "@service/aeo";
import { useVisiblePoll } from "../lib/useVisiblePoll";
import { ouraHref, type View } from "../nav";
import type { Business } from "../contract";
import {
    chooseSignatureQuestion, getOnboarding, getOnboardingPlanFacts, onboardingSim, recordOnboardingPayment, resendOnboardingLogin, sendOnboardingMessage,
    type MessageKind, type Onboarding, type SentMessage,
    isOnboardingLive, markTimelineUploaded, sendResults, sendResultsTest,
} from "@service/aeo/onboarding";
import { useSnackbar } from "@hook/use-snackbar";
import { dayDate } from "../lib/oura";
import { DELIVERY_ON } from "../delivery/flag";
import { TIMELINE_READ_ON } from "../onboarding/timelineRead";
import { TimelineUploadDrawer } from "../components/onboarding/TimelineUploadDrawer";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import {
    ACCESS_LABEL, FIRST_CHECK_DRAFT_FLOW, accessPersonName, accessRowOf, callName, dayMonth, firstCheckDraftHref,
    accessAsked, firstCheckOpensAt, isHeld, isOneSetup, lastSent, nextAction, onboardingMilestones, onboardingProgress, opensOnArrival, PRESS_NEXT, PRESS_PARAM,
    type AccessRowKey, type NextAction, type Milestone, type OnboardingPlanFacts, type StepWho,
} from "../onboarding/derive";
import { releaseLine, resendDraftOf } from "../onboarding/drafts";
import { inputClass } from "../onboarding/messageText";
import { SITE_GUIDES } from "../onboarding/guides";
import { BusinessRecordCard } from "../components/onboarding/BusinessRecordCard";
import { AccessDrawer, AccessTag, AddLoginDrawer, ComposeDrawer, EditContactDrawer, ReleaseDrawer, SentDrawer, StartScanDrawer } from "../components/onboarding/drawers";
import { AnswersDrawer } from "../components/onboarding/AnswersDrawer";
import { useOuraRole } from "../role";
import { NewClientDialog } from "../components/onboarding/NewClientDialog";
import { StepsList } from "../components/onboarding/StepsList";
import { EmailsCard } from "../components/onboarding/EmailsCard";
import { UseAnotherScan } from "../components/onboarding/UseAnotherScan";
import { BaselineSnapshotRow } from "../components/onboarding/BaselineSnapshot";
import { todayIso } from "../components/plan/planDates";
import { NextCardFrame } from "../overview/NextCardFrame";
import { UploadReportDialog } from "../components/client/ClientDialogs";
import { SnapshotDrawer } from "../components/onboarding/SnapshotDrawer";
import { useHub } from "../overview/useHubStage";
import { ResendResultsDialog } from "../components/onboarding/ResendResultsDialog";
import { AccountReadyDialog } from "../components/onboarding/AccountReady";

/** Which drawer is open. One at a time. */
type Open =
    | { kind: "compose"; message: MessageKind }
    | { kind: "sent"; message: SentMessage }
    | { kind: "release" }
    | { kind: "answers" }
    | { kind: "scan" }
    | { kind: "login" }
    | { kind: "access"; row: AccessRowKey }
    | null;

const WHO: Record<StepWho, { label: string; tone: Tone }> = {
    staff: { label: "Needs us", tone: "blue" },
    merchant: { label: "Waiting on client", tone: "gray" },
    oura: { label: "Oura is working", tone: "violet" },
};

/** One row of the Client card: the label, then the value on the same line, both in body type. */
function Fact({ label, value, action }: { label: string; value: ReactNode; action?: ReactNode }) {
    return (
        // Stacked on a phone, so an email is not squeezed into half the width.
        <div className="flex flex-col gap-0.5 py-2.5 first:pt-0 last:pb-0 sm:flex-row sm:items-baseline sm:gap-4">
            <dt className={cn(TYPE.tb, "m-0 flex-shrink-0 text-gray-500 sm:w-36")}>{label}</dt>
            <dd className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-gray-900 [overflow-wrap:anywhere]")}>
                {value}
                {action && <> {action}</>}
            </dd>
        </div>
    );
}

/** The second half of a value (an email, a date), muted after a middot. */
function Also({ children }: { children: ReactNode }) {
    return <span className="text-gray-500"> · {children}</span>;
}

/** "6-Month plan · 3 outlets": the term only when the plan's own name does not already say it. */
function planLine(plan: NonNullable<Onboarding["plan"]>, buyMode?: Onboarding["buyMode"]): string {
    // An online client has no outlets to count: the plan reads "online" instead.
    const outlets = plan.outlets > 0 && buyMode !== "online" ? `${plan.outlets} ${plan.outlets === 1 ? "outlet" : "outlets"}` : "online";
    const name = plan.planName?.trim();
    if (!name) return `${plan.termMonths} months · ${outlets}`;
    const saysTerm = new RegExp(`\\b${plan.termMonths}\\s*-?\\s*months?\\b`, "i").test(name);
    return saysTerm ? `${name} · ${outlets}` : `${name} · ${plan.termMonths} months · ${outlets}`;
}

/** The ground the kit's cards sit on, run to the edges of the older chrome. */
function Ground({ children }: { children: ReactNode }) {
    return <div className={cn(PAGE_GROUND, "-mx-4 -mb-6 min-h-[calc(100vh-7rem)] px-4 pb-10 pt-5 lg:-mx-8 lg:px-8")}>{children}</div>;
}

export function OnboardingTab({ business, businessId }: { business: Business; businessId: string }) {
    // undefined = still asking. null = this business has no onboarding record.
    const [onboarding, setOnboarding] = useState<Onboarding | null | undefined>(undefined);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState<Open>(null);
    const [starting, setStarting] = useState(false);
    const seq = useRef(0);
    /**
     * With no onboarding record: whether a plan already exists (set up before
     * onboarding did). null while asking. "failed" is its own state: a read
     * that failed must not offer to start onboarding a client who has a plan.
     */
    const [planned, setPlanned] = useState<"yes" | "no" | "failed" | null>(null);
    const [planTry, setPlanTry] = useState(0);
    const [addingLogin, setAddingLogin] = useState(false);
    const navigate = useNavigate();
    /**
     * A Next card press made on the Overview card or a list row (`?press=next`):
     * run once, by the first Next card this visit draws, then dropped from the
     * URL so a reload or a later visit never presses it again.
     */
    const [params, setParams] = useSearchParams();
    const pressRef = useRef(params.get(PRESS_PARAM) === PRESS_NEXT);
    useEffect(() => {
        if (!params.has(PRESS_PARAM)) return;
        setParams((was) => { const next = new URLSearchParams(was); next.delete(PRESS_PARAM); return next; }, { replace: true });
    }, [params, setParams]);
    // No record to press on: the press is spent, never saved for a record created later.
    useEffect(() => {
        if (onboarding === null) pressRef.current = false;
    }, [onboarding]);
    const takePress = useCallback(() => {
        const pressed = pressRef.current;
        pressRef.current = false;
        return pressed;
    }, []);
    /**
     * Where the business page puts this business. The stage (when the New scan
     * switch is on) is what the header and the Overview card read, so this page
     * agrees with them: a business the stage calls a client is never offered
     * Client signed again, and the header's blue pill keeps the one blue press.
     */
    const hub = useHub();
    const stageKind = hub?.stage?.kind ?? null;
    const clientByStage = stageKind === "active" || stageKind === "active_unscanned";
    const headerHasPrimary = !!hub?.pills.some((p) => p.primary);
    // Set up before onboarding: a plan, or a business the stage reads as a client.
    const setUp = planned === "yes" || (planned === "no" && clientByStage);
    /** A client with a plan and no onboarding: whether it has ever had a usable AI check (1.1 row Q). */
    const [neverScanned, setNeverScanned] = useState(false);
    useEffect(() => {
        // Never carried from another business, or from before its plan was read.
        setNeverScanned(false);
        if (!setUp) return;
        let cancelled = false;
        listBusinesses().then(
            (rows) => {
                const a = rows.find((b) => b.id === businessId)?.latest_assessment;
                if (!cancelled) setNeverScanned(!a || a.gated || (a.status !== "complete" && a.status !== "partial"));
            },
            () => { if (!cancelled) setNeverScanned(false); },
        );
        return () => { cancelled = true; };
    }, [setUp, businessId]);
    useEffect(() => {
        if (onboarding !== null) return;
        let cancelled = false;
        setPlanned(null);
        getEngagementIfExists(businessId).then(
            (plan) => { if (!cancelled) setPlanned(plan ? "yes" : "no"); },
            () => { if (!cancelled) setPlanned("failed"); },
        );
        return () => { cancelled = true; };
    }, [onboarding, businessId, planTry]);

    const refresh = useCallback(async () => {
        const mine = ++seq.current;
        try {
            const next = await getOnboarding(businessId);
            if (seq.current !== mine) return;
            setOnboarding(next);
            setFailed(false);
        } catch {
            if (seq.current === mine) setFailed(true);
        }
    }, [businessId]);

    /** What an action answered with. Newer than any read still on its way, so those are dropped. */
    const apply = useCallback((next: Onboarding) => {
        seq.current += 1;
        setOnboarding(next);
    }, []);

    useEffect(() => {
        setOnboarding(undefined);
        setFailed(false);
        setOpen(null);
        void refresh();
        return () => { seq.current += 1; };
    }, [refresh]);

    // A running AI check moves by itself, so the screen keeps up with it.
    // Never under an open drawer: what is being read must not change mid-sentence.
    // A first-check draft being built moves by itself too, in about a minute.
    // So does the Baseline Snapshot Oura makes after the results are approved.
    // And the login email while it is on its way (ourteam-backend#5523): a few seconds, then `sent` or `send_failed`.
    const snapshotMaking = Boolean(onboarding?.resultsApprovedAt && !onboarding?.snapshotReadyAt && onboarding?.resultsSentAt === null);
    const backgroundRunning = onboarding?.scan.status === "running" || onboarding?.scan.draft?.status === "building" || snapshotMaking;
    const idle = !open;
    // The login email on its way (ourteam-backend#5523) is read again even under an
    // open drawer, or the drawer's "Still on its way" line would never move on.
    const loginSending = onboarding?.login.status === "sending";
    const shouldPoll = loginSending || (backgroundRunning && idle);
    useVisiblePoll(() => { if (shouldPoll) void refresh(); }, 3_000, [shouldPoll, refresh]);
    const running = backgroundRunning || loginSending;
    // Everything else changes when the client acts, which is minutes apart.
    useVisiblePoll(() => { if (onboarding && !running && idle) void refresh(); }, 60_000, [!!onboarding, running, idle, refresh]);

    const href = (view: View) => ouraHref(view, { role: "oura-staff", businessId });
    const close = useCallback(() => {
        setOpen(null);
        // The button that opened a drawer may be gone once it has done its job.
        // Focus then goes to the Next card, where the page now starts.
        window.setTimeout(() => {
            const active = document.activeElement;
            if (active && active !== document.body) return;
            const card = document.querySelector<HTMLElement>("[data-next-card]");
            (card?.querySelector<HTMLElement>("button") ?? card)?.focus();
        }, 60);
    }, []);

    let body: ReactNode;
    if (failed && onboarding === undefined) {
        body = (
            <Surface className="items-start gap-3">
                <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Couldn't load onboarding</h2>
                <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Nothing is lost. Try again.</p>
                <PillButton variant="primary" onClick={() => void refresh()}>Try again</PillButton>
            </Surface>
        );
    } else if (onboarding === null && planned === "failed") {
        body = (
            <Surface className="items-start gap-3">
                <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Couldn't check for a plan</h2>
                <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Oura could not tell whether this client is set up already. Try again before starting.</p>
                <PillButton variant="primary" onClick={() => setPlanTry((n) => n + 1)}>Try again</PillButton>
            </Surface>
        );
    } else if (onboarding === undefined || (onboarding === null && (planned === null || (hub !== null && hub.stage === null && !hub.failed)))) {
        // With no onboarding record, the body depends on the stage: wait for it rather than flip
        // from "Not a client yet" to another body once it lands (#4517).
        body = (
            <div role="status" aria-label="Loading onboarding" className="space-y-5">
                <div className="h-[104px] animate-pulse rounded-2xl bg-white/70" />
                <div className="h-[360px] animate-pulse rounded-2xl bg-white/70" />
            </div>
        );
    } else if (onboarding === null && setUp) {
        // Signed and set up before onboarding existed: nothing to walk through,
        // and the one thing still asked for is a login for someone new. With no
        // AI check yet, its first one is drafted like any client's (1.1 row Q).
        body = (
            <>
                <PageSentence>Set up before onboarding.</PageSentence>
                <Surface className="items-start gap-3">
                    <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>{planned === "yes" ? "This client has a plan already" : "No onboarding record"}</h2>
                    <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Add a login for another person at the client.</p>
                    <PillButton data-add-login="" onClick={() => setAddingLogin(true)}>Add a login</PillButton>
                    {FIRST_CHECK_DRAFT_FLOW && neverScanned && (
                        <div className="mt-1 flex w-full flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
                            <p className={cn(TYPE.tb, "m-0 text-gray-600")}>No AI check yet.</p>
                            <PillButton variant={headerHasPrimary ? "secondary" : "primary"} onClick={() => navigate(firstCheckDraftHref(businessId))}>Draft the 50</PillButton>
                        </div>
                    )}
                </Surface>
                {addingLogin && <AddLoginDrawer open businessId={businessId} businessName={business.name} onClose={() => setAddingLogin(false)} />}
            </>
        );
    } else if (onboarding === null) {
        body = (
            <>
                <PageSentence>Not a client yet.</PageSentence>
                <Surface className="items-start gap-3">
                    <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>When {business.name} signs</h2>
                    <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Oura sends the form, drafts the first AI check and readies the login.</p>
                    {/* One blue press per screen: the header's stage pill keeps it when it has one. */}
                    <PillButton variant={headerHasPrimary ? "secondary" : "primary"} data-start-onboarding="" onClick={() => setStarting(true)}>Client signed</PillButton>
                </Surface>
            </>
        );
    } else {
        body = (
            // Keyed by the business: the payment date, an open form or a refusal never carries into another client (Jack, #4289).
            <Loaded
                key={onboarding.businessId}
                onboarding={onboarding}
                open={open}
                href={href}
                onOpen={setOpen}
                onClose={close}
                onApply={apply}
                onRefresh={() => void refresh()}
                takePress={takePress}
                basePath={business.basePath ?? ""}
            />
        );
    }

    return (
        <FoldScope value="staff-onboarding">
            <Ground>
                {body}
                {/* Outside every branch: creating the client changes the branch,
                    and the dialog still has its own last screen to show. */}
                <NewClientDialog
                    open={starting}
                    onClose={() => setStarting(false)}
                    existing={{ businessId, name: business.name, domain: business.domain }}
                    onCreated={({ onboarding: created }) => apply(created)}
                    onOpen={(id) => { setStarting(false); if (id !== businessId) navigate(ouraHref("onboarding", { role: "oura-staff", businessId: id })); }}
                    returnFocus={() => document.querySelector<HTMLElement>("[data-start-onboarding]")}
                />
            </Ground>
        </FoldScope>
    );
}

function Loaded({
    onboarding: o,
    open,
    href,
    onOpen,
    onClose,
    onApply,
    onRefresh,
    takePress,
    basePath,
}: {
    onboarding: Onboarding;
    open: Open;
    href: (view: View) => string;
    onOpen: (next: Open) => void;
    onClose: () => void;
    onApply: (next: Onboarding) => void;
    onRefresh: () => void;
    /** True once when this visit came with a press from elsewhere. */
    takePress: () => boolean;
    /** The site's path on a shared host (""): the scans a First AI check can be picked from match it (#4773). */
    basePath: string;
}) {
    const snackbar = useSnackbar();
    // Staff only, as the route is: a client never corrects their own record.
    // The screen is reached through a staff guard today, so this is the second
    // lock rather than the only one — and it is what makes the control vanish
    // in "preview as the merchant" (#3992).
    const isStaff = useOuraRole() === "oura-staff";
    const navigate = useNavigate();
    const [editingContact, setEditingContact] = useState(false);
    /**
     * The plan's signature and schedule, read once the first check is done
     * (F11, the publish card). Null until read. A read that FAILED is its own
     * state: with no plan known, the Next card would say "complete" while the
     * signature or the schedule is still due, so a retry stands in its place (Jack, #4291).
     */
    // A read has SUCCEEDED (its answer may be null, "no plan") FOR THIS BUSINESS. Until then the
    // Next card is not built: a plan still on its way is not "nothing left to do" (Jack, #4289),
    // and another business's plan is not this one's when the tab is reused (Jack, #4289/#4290).
    // Re-reads of the same business keep the last answer.
    const [planResult, setPlanResult] = useState<{
        businessId: string;
        facts: OnboardingPlanFacts | null;
    } | null>(null);
    const [planFailedFor, setPlanFailedFor] = useState<string | null>(null);
    const [planRead, setPlanRead] = useState(0);
    const scanDone = o.scan.status === "complete";
    const planLoaded = scanDone && planResult?.businessId === o.businessId;
    const plan = planResult?.businessId === o.businessId ? planResult.facts : null;
    const planFailed = scanDone && planFailedFor === o.businessId;
    useEffect(() => {
        if (!scanDone) { setPlanResult(null); setPlanFailedFor(null); return; }
        let cancelled = false;
        setPlanFailedFor((current) => (current === o.businessId ? null : current));
        getOnboardingPlanFacts(o.businessId).then(
            (facts) => {
                if (cancelled) return;
                setPlanResult({ businessId: o.businessId, facts });
                setPlanFailedFor((current) => (current === o.businessId ? null : current));
            },
            () => { if (!cancelled) setPlanFailedFor(o.businessId); },
        );
        return () => { cancelled = true; };
    }, [o.businessId, scanDone, o.access, o.login.status, planRead]);
    const planUnknown = scanDone && !planLoaded && planFailed;
    const planLoading = scanDone && !planLoaded && !planFailed;
    /** Record the payment: the date row open on the Next card. */
    const [paying, setPaying] = useState(false);
    // Results and plan: each document goes through the Reports upload. Which one is being uploaded.
    const [uploading, setUploading] = useState<"timeline" | null>(null);
    const [sendingResults, setSendingResults] = useState(false);
    // Results and plan, sent: the "Send again" confirmation (#4469).
    const [resending, setResending] = useState(false);
    /** The timeline uploaded but was not added to the results: its row offers Retry, not a second upload. */
    const [unrecorded, setUnrecorded] = useState(false);
    const [cardRefusal, setCardRefusal] = useState<string | null>(null);
    const [cardBusy, setCardBusy] = useState(false);
    const derived = nextAction(o, Date.now(), { plan });
    // A client set up before the onboarding flow (accountReady.eligible) is not sent the Setup
    // email: the Next card's button sends the one-off "Your Oura account is ready" instead (#4675).
    const tour = isStaff && derived.kind === "send_kickoff" && o.accountReady?.eligible === true;
    const tourSentAt = o.accountReady?.sentAt ?? null;
    const tourSending = o.accountReady?.sendingSince ?? null;
    const next: NextAction = tour
        ? {
            ...derived,
            cta: tourSentAt ? "Send again" : "Send email",
            // The same email to our own inbox first (user-notif), as the Results card's test.
            secondary: "Send Internal Test",
            why: tourSending
                ? `An account ready email started ${dayMonth(tourSending)} has not finished. Check the client's inbox before sending again.`
                : tourSentAt ? `Account ready email sent ${dayMonth(tourSentAt)}.` : derived.why,
        }
        : derived;
    const [touring, setTouring] = useState<"send" | "test" | null>(null);
    // The first check done and the results unsent, on a v3 record, while the Next card is on an earlier
    // step: the results get their own card so they need not wait for it (#4526).
    const resultsEarly = next.kind !== "send_results" && o.scan.status === "complete" && o.resultsSentAt === null && o.resultsApprovedAt !== undefined;
    // Sent with no Execution Timeline recorded: the retrofit of a client onboarded by hand marks the
    // results sent (no email) whatever the timeline (Bee Choo, Luxe Dental, 23 Oct). The Results card is
    // gone once sent, so the upload gets its own card; preview and "Use this Timeline" have no send gate.
    const timelineMissing = TIMELINE_READ_ON && o.resultsSentAt !== null && !o.resultsPack?.timelineAt;
    // Invites the client says are done: ours to confirm from the moment they finish, whatever the Next card leads with.
    // Invites only: a Search Console or Analytics claim waits on its connection, not on a confirmation (#4513).
    const claimedInvites = accessAsked(o).filter((a) => a.status === "claimed" && (a.key === "gbp" || a.key === "website")).map((a) => accessRowOf(a.key));
    // A Search Console or Analytics claim: staff connect it on Connectors, so the row goes there (#4513).
    const claimedGoogle = (a: Onboarding["access"][number]) => a.status === "claimed" && (a.key === "gsc" || a.key === "ga4");
    const steps = onboardingMilestones(o);
    const { done, total } = onboardingProgress(o);
    useEffect(() => { setPaying(false); setCardRefusal(null); }, [next.kind]);

    /** A First AI check picked by hand (#4773): approval and the Snapshot start again, so the plan is read again too. */
    const pickedFirstCheck = (next: Onboarding) => {
        onApply(next);
        setPlanRead((n) => n + 1);
    };

    /** The First AI check opens the draft flow with the flag on; the drawer with it off. */
    const openFirstCheck = () => {
        if (FIRST_CHECK_DRAFT_FLOW) navigate(firstCheckDraftHref(o.businessId, firstCheckOpensAt(o), o.scan.draft?.id));
        else onOpen({ kind: "scan" });
    };

    const takeSignature = async () => {
        const closest = plan?.closest;
        if (!closest) { navigate(href("contract")); return; }
        setCardBusy(true);
        setCardRefusal(null);
        const r = await chooseSignatureQuestion(o.businessId, closest.n);
        setCardBusy(false);
        if (!r.ok) { setCardRefusal(r.message); return; }
        snackbar.showSuccess(`Q${closest.n} is the signature question`, "It is on the plan now.");
        onApply(r.onboarding);
        setPlanRead((n) => n + 1);
    };

    /** The payment row closed (cancelled or saved): focus goes to the card's button, where the page now starts. */
    const refocusCard = () => {
        window.setTimeout(() => {
            const card = document.querySelector<HTMLElement>("[data-next-card]");
            (card?.querySelector<HTMLElement>("button") ?? card)?.focus();
        }, 60);
    };

    const recordPayment = async (paidOn: string) => {
        setCardBusy(true);
        setCardRefusal(null);
        const r = await recordOnboardingPayment(o.businessId, paidOn);
        setCardBusy(false);
        if (!r.ok) { setCardRefusal(r.message); return; }
        setPaying(false);
        // One setup: the plan starts the Monday after the results and plan go, not on the payment date.
        snackbar.showSuccess("Payment recorded", isOneSetup(o) ? "The Setup email is next." : "The plan starts on that date. The kick-off is next.");
        onApply(r.onboarding);
        refocusCard();
    };

    /** The upload is in Reports: add it to the results. If that fails, nothing is uploaded twice: the row offers Retry. */
    const recordTimeline = async () => {
        setCardRefusal(null);
        try {
            // The backend reads the timeline's dates into the schedule, so the plan is read again too.
            await markTimelineUploaded(o.businessId);
            setUnrecorded(false);
            onRefresh();
            setPlanRead((n) => n + 1);
        } catch {
            setUnrecorded(true);
            setCardRefusal("The execution timeline is uploaded, but it wasn't added to the results. Press Retry. No need to upload it again.");
            refocusCard();
        }
    };

    const deliverResults = async () => {
        if (sendingResults) return;
        setSendingResults(true);
        setCardRefusal(null);
        try {
            await sendResults(o.businessId);
            onRefresh();
        } catch {
            setCardRefusal("Couldn't confirm the results and plan went to the client. Check Emails below before you try again.");
            refocusCard();
        } finally {
            setSendingResults(false);
        }
    };

    const act = () => {
        switch (next.kind) {
            case "record_payment": setPaying(true); return;
            case "draft_failed":
            case "review_draft": openFirstCheck(); return;
            case "choose_signature": void takeSignature(); return;
            // Straight to the publish card, not the top of the month view.
            case "publish": navigate(`${href("timeline")}#publish`); return;
            case "send_kickoff":
                if (tour) setTouring("send");
                else onOpen({ kind: "compose", message: "kickoff" });
                return;
            case "wait_form":
            case "nudge_form": onOpen({ kind: "compose", message: "form_reminder" }); return;
            case "wait_access":
            case "nudge_access": onOpen({ kind: "compose", message: "access_reminder" }); return;
            case "release": onOpen({ kind: "release" }); return;
            case "resend_login": {
                // The send that failed, so the drawer shows the server's
                // reason and offers Send again -- which calls the resend, not
                // the release flow. With no row recorded at all there is
                // nothing to show and nothing was ever sent, so it is the
                // first send: the release drawer.
                const failed = lastSent(o, "login_invite");
                onOpen(failed ? { kind: "sent", message: failed } : { kind: "release" });
                return;
            }
            case "confirm_access": if (next.row) onOpen({ kind: "access", row: next.row }); return;
            case "start_scan":
            case "retry_scan": openFirstCheck(); return;
            default: return;
        }
    };

    // Pressed on the Overview card or a list row: the same press, once, here (F14).
    // Once the plan is read: a press is never carried out on a Next card built before it.
    // Per business: arriving at A does not use up B's arrival when the tab is reused.
    const arrivedForBusiness = useRef<string | null>(null);
    const nextVerified = !planLoading && !planUnknown;
    useEffect(() => {
        if (arrivedForBusiness.current === o.businessId || !nextVerified) return;
        arrivedForBusiness.current = o.businessId;
        if (takePress() && opensOnArrival(next.kind) && next.cta) act();
        // Arrival only, once the Next card stands on this business's read plan.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [o.businessId, nextVerified]);

    const openStep = (step: Milestone): (() => void) | undefined => {
        switch (step.key) {
            case "paid": return undefined;
            case "kickoff": {
                const sent = lastSent(o, "kickoff");
                return () => onOpen(sent ? { kind: "sent", message: sent } : { kind: "compose", message: "kickoff" });
            }
            case "setup":
                // The answers, once in. Each access is in the Access card beside this one.
                if (o.form.answers) return () => onOpen({ kind: "answers" });
                {
                    // Waiting: what they were sent. A reminder is the Next card's to offer, when it is due.
                    const asked = lastSent(o, "form_reminder", "kickoff");
                    return asked ? () => onOpen({ kind: "sent", message: asked }) : undefined;
                }
            case "scan": return step.state === "ours" ? openFirstCheck : undefined;
            case "baseline":
                if (resultsEarly) return () => document.getElementById(RESULTS_CARD_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
                if (step.state === "ours") return () => refocusCard();
                // Sent, and this backend can send it again (#4469): the row opens "Send again".
                return step.state === "done" && o.resultsResentAt !== undefined ? () => setResending(true) : undefined;
            case "release": {
                if (step.state === "ours") return () => onOpen({ kind: "release" });
                const sent = lastSent(o, "access_request", "login_invite");
                return sent ? () => onOpen({ kind: "sent", message: sent }) : undefined;
            }
        }
    };

    // Signed is the earlier of the record and the payment: a payment recorded later
    // (signed, not paid yet) never moves the signing date.
    const signedAt = o.paidAt && o.paidAt < o.createdAt ? o.paidAt : o.createdAt;
    const resultsPanel = (
        <ResultsPanel
            onboarding={o}
            sending={sendingResults}
            unrecorded={unrecorded}
            onUpload={() => setUploading("timeline")}
            onRetry={() => void recordTimeline()}
            onOpenResults={() => navigate(href("questions"))}
            onOpenTimeline={() => navigate(href("timeline"))}
            onSend={() => void deliverResults()}
            onAddKeywords={() => navigate(`${href("delivery")}?view=plan&focus=${encodeURIComponent(KEYWORDS_FIELD_ID)}`)}
        />
    );
    return (
        <>
            {/* The count and the one date nothing else on the page carries.
                Who is being waited on is the Next card's to say, once. */}
            <PageSentence>
                {planUnknown || planLoading
                    ? `${done} of ${total} done. Signed ${dayMonth(signedAt)}.`
                    : next.kind === "done"
                    ? `Active since ${dayMonth(o.completedAt)}. Signed ${dayMonth(signedAt)}.`
                    : next.kind === "publish"
                      // Every step is done but one thing is left: say it, so "9 of 9" never reads as finished.
                      ? `${done} of ${total} done. The schedule is not published yet. Signed ${dayMonth(signedAt)}.`
                      : `${done} of ${total} done. Signed ${dayMonth(signedAt)}.`}
            </PageSentence>

            <div className="space-y-5">
                {planLoading ? (
                    <Surface className="items-start gap-3">
                        <p role="status" aria-busy className={cn(TYPE.tb, "m-0 text-gray-600")}>Loading the plan and schedule…</p>
                    </Surface>
                ) : planUnknown ? (
                    <Surface className="items-start gap-3">
                        <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Couldn't read the plan</h2>
                        <p role="alert" className={cn(TYPE.tb, "m-0 text-gray-600")}>The signature and the schedule are not known yet, so neither is the next step. Try again.</p>
                        <PillButton variant="primary" onClick={() => setPlanRead((n) => n + 1)}>Try again</PillButton>
                    </Surface>
                ) : (
                <NextCard
                    next={next}
                    onboarding={o}
                    onAct={act}
                    onSecondary={next.kind === "choose_signature" ? () => navigate(href("contract")) : tour ? () => setTouring("test") : undefined}
                    paying={paying}
                    busy={cardBusy}
                    refusal={cardRefusal}
                    onPay={(d) => void recordPayment(d)}
                    onCancelPay={() => { setPaying(false); setCardRefusal(null); refocusCard(); }}
                    invites={next.kind === "confirm_access" ? [] : claimedInvites}
                    onConfirmInvite={(row) => onOpen({ kind: "access", row })}
                    resultsPanel={next.kind === "send_results" ? resultsPanel : null}
                />
                )}
                {touring && (
                    <AccountReadyDialog
                        onboarding={o}
                        test={touring === "test"}
                        onClose={() => setTouring(null)}
                        onSent={() => {
                            const was = touring;
                            setTouring(null);
                            if (was === "test") {
                                snackbar.showSuccess("Test sent", `The account-ready email went to ${o.accountReady?.testTo ?? "our inbox"} only.`);
                                return;
                            }
                            onRefresh();
                            snackbar.showSuccess("The account-ready email went out", `${callName(o.accountReady?.to?.name ?? "") || "The client"} gets their login and the way to Setup.`);
                        }}
                    />
                )}

                {/* The results can be worked on as soon as the first check is done, whatever an
                    earlier step still waits for (#4526): the send needs only the approval, the
                    Snapshot and the timeline. Its own card while the Next card is elsewhere. */}
                {resultsEarly && !planUnknown && (
                    <div id={RESULTS_CARD_ID} className="scroll-mt-20">
                        <Surface className="items-stretch gap-1">
                            <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Results and plan</h2>
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>The first AI check is done, so this can go ahead now. The steps above can follow.</p>
                            {resultsPanel}
                        </Surface>
                    </div>
                )}

                {timelineMissing && !planUnknown && (
                    <div className="scroll-mt-20" data-timeline-missing="">
                        <Surface className="items-stretch gap-2">
                            <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Execution timeline</h2>
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                                The results and plan are marked sent, but no Execution Timeline is recorded, so Oura&rsquo;s plan does not follow one yet.
                            </p>
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                                Upload the PDF and its data file: Oura checks them against the quote and shows what changes before anything is saved. No email goes to the client.
                            </p>
                            <div>
                                {/* One blue button per screen: the Next card's own blue button
                                    leads when it has one, so this one steps down (audit, 25 Oct). */}
                                <PillButton variant={next.cta && next.primary ? "secondary" : "primary"} onClick={() => setUploading("timeline")}>Upload the Execution Timeline</PillButton>
                            </div>
                        </Surface>
                    </div>
                )}

                <Bento layout="c21" className="!items-start">
                    {/* Steps, and under it each access its Setup row counts. */}
                    <BentoStack>
                        <FoldCard id="steps" title="Steps" icon={{ icon: ListChecksIcon, area: "work" }} foldedFact={`${done} of ${total}`}>
                            <StepsList
                                steps={steps}
                                actionFor={(step) => {
                                    // A finished check opens what it found, also while it reads
                                    // In progress for our review. Everything else opens its drawer.
                                    if (step.key === "scan" && o.scan.status === "complete") return { to: href("questions") };
                                    return { onClick: openStep(step) };
                                }}
                            />
                            {isStaff && (
                                <UseAnotherScan
                                    businessId={o.businessId}
                                    domain={o.domain}
                                    basePath={basePath}
                                    onboarding={o}
                                    onPicked={pickedFirstCheck}
                                    onRefresh={onRefresh}
                                />
                            )}
                        </FoldCard>

                        {/* The access list, once. While set-up runs it is each access
                            the Setup step counts; once every access is held it becomes
                            the record of what we hold, and what to remove when the
                            contract ends. */}
                        {accessAsked(o).length > 0 && <FoldCard
                            id="held"
                            title={nextVerified && (next.kind === "done" || next.kind === "publish") ? "What we hold" : "Access"}
                            icon={{ icon: KeyRoundIcon, area: "setup" }}
                            foldedFact={accessFact(accessAsked(o))}
                        >
                            {accessAsked(o).map((a) => (
                                <ListRow
                                    key={a.key}
                                    title={ACCESS_LABEL[a.key]}
                                    sub={claimedGoogle(a) ? "Says they added us · connect from Connectors" : a.role}
                                    right={<AccessTag status={a.status} />}
                                    muted={a.status === "not_started"}
                                    onClick={claimedGoogle(a) ? () => navigate(href("connectors")) : () => onOpen({ kind: "access", row: accessRowOf(a.key) })}
                                />
                            ))}
                            <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>No passwords stored.</p>
                        </FoldCard>}
                    </BentoStack>

                    <BentoStack>
                        <FoldCard id="client" title="Client" icon={{ icon: UserIcon, area: "setup" }} foldedFact={o.contact.name}>
                            {/* One fact a row, a label and its value on one line, all in body
                                type: the term is said once, in the plan's name, and the domain
                                once, under Website (Jack). */}
                            <dl className="m-0 divide-y divide-gray-100">
                                <Fact
                                    label="Contact"
                                    value={<>{o.contact.name}<Also>{o.contact.email}</Also></>}
                                    action={isStaff && (
                                        // Staff only, as the route is: a client never corrects their own record (#3992).
                                        <button
                                            type="button"
                                            onClick={() => setEditingContact(true)}
                                            className={cn("rounded font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-800", FOCUS_RING)}
                                        >
                                            Edit
                                        </button>
                                    )}
                                />
                                <Fact
                                    label="Looks after access"
                                    value={accessPersonName(o)
                                        ? <>{accessPersonName(o)}{o.accessContact?.email && <Also>{o.accessContact.email}</Also>}</>
                                        : <span className="text-gray-400">Not known yet</span>}
                                />
                                <Fact label="Plan" value={o.plan ? planLine(o.plan, o.buyMode) : <span className="text-gray-400">No plan</span>} />
                                <Fact label="Website" value={<>{o.domain}{o.siteBuilder && <Also>{SITE_GUIDES[o.siteBuilder].label}</Also>}</>} />
                                <Fact label="Account manager" value={<>{o.accountManager.name}<Also>{o.accountManager.email}</Also></>} />
                                <Fact
                                    label="Quote"
                                    value={o.plan?.ref
                                        ? <>{o.plan.ref}{o.plan.startedAt && <Also>starts {dayMonth(o.plan.startedAt)}</Also>}</>
                                        : <span className="text-gray-400">None</span>}
                                />
                            </dl>
                            {/* The first login goes out with the access request. After it, anyone else at the client. */}
                            {o.login.status !== "not_sent" && (
                                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                                    <span className={cn(TYPE.tc, "text-gray-500")}>
                                        Login: {o.login.contact ? callName(o.login.contact.name) : "sent"}, {o.login.status === "signed_in" ? "signed in" : "not signed in yet"}.
                                    </span>
                                    <PillButton variant="text" className="max-sm:h-9" onClick={() => onOpen({ kind: "login" })}>Add a login</PillButton>
                                </div>
                            )}
                        </FoldCard>

                        {/* Whether the name, website and each outlet's address and
                            phone agree between the signed quote and Google (#4153).
                            Staff only; it draws nothing for a business with no
                            engagement or while the backend does not send it. */}
                        {isStaff && <BusinessRecordCard businessId={o.businessId} />}

                        <EmailsCard onboarding={o} onOpen={(m) => onOpen({ kind: "sent", message: m })} />
                    </BentoStack>
                </Bento>

                {resending && (
                    <ResendResultsDialog
                        onboarding={o}
                        onClose={() => setResending(false)}
                        onSent={() => {
                            setResending(false);
                            onRefresh();
                            snackbar.showSuccess("The results and plan were sent again", `${callName(o.contact.name) || "The client"} gets the same email.`);
                        }}
                    />
                )}
                {onboardingSim.available && <SamplePanel onboarding={o} next={next} onChanged={() => { onRefresh(); setPlanRead((n) => n + 1); }} />}
                {TIMELINE_READ_ON && (
                    <TimelineUploadDrawer
                        open={uploading !== null}
                        businessId={o.businessId}
                        clientName={o.businessName}
                        onClose={() => setUploading(null)}
                        onConfirmed={(version) => {
                            setUploading(null);
                            snackbar.showSuccess(`Oura's plan follows Timeline ${version}`, "No email went to the client.");
                            onRefresh();
                            setPlanRead((n) => n + 1);
                        }}
                    />
                )}
                <UploadReportDialog
                    open={!TIMELINE_READ_ON && uploading !== null}
                    businessId={o.businessId}
                    // The results email tells the client about both documents: no email per upload (#4398).
                    notify={false}
                    onOpenChange={(v) => { if (!v) setUploading(null); }}
                    // Upload, then add it to the results. recordTimeline never throws: a failed add
                    // marks the row "unrecorded", whose Retry re-adds the same upload, never a second file.
                    onUploaded={() => (uploading ? recordTimeline() : undefined)}
                />
            </div>

            {open?.kind === "compose" && (
                <ComposeDrawer key={open.message} open kind={open.message} onboarding={o} onClose={onClose} onSent={onApply} />
            )}
            {editingContact && (
                <EditContactDrawer
                    open
                    onboarding={o}
                    onSaved={onApply}
                    onClose={() => setEditingContact(false)}
                />
            )}
            {open?.kind === "sent" && (
                <SentDrawer
                    key={open.message.id}
                    open
                    message={open.message}
                    businessName={o.businessName}
                    status={open.message.kind === "login_invite"
                        ? (o.login.status === "signed_in"
                            ? `Signed in ${dayMonth(o.login.signedInAt)}.`
                            : o.login.status === "sending"
                                ? "Still on its way. Nothing for the client to sign in to yet."
                            : o.login.status === "send_failed"
                                // Never "not signed in yet" on a send that
                                // failed: that reads as waiting on the client.
                                ? "The client was never sent this, so there is nothing for them to sign in to."
                                : "Not signed in yet. The link lasts 7 days.")
                        : undefined}
                    // The server's sentence when the last send failed (BE-1).
                    failure={open.message.kind === "login_invite" ? o.login.failureDetail : undefined}
                    sentTo={open.message.kind === "login_invite" ? o.login.contact?.email ?? o.contact.email : o.contact.email}
                    formLink={o.form.link}
                    // The kick-off is the one that can be sent again: it is the
                    // one a mistyped address loses, and the client cannot start
                    // without it (#3992). A new draft, read from the contact as
                    // it stands now.
                    onSendAgain={open.message.kind === "kickoff" ? () => onOpen({ kind: "compose", message: "kickoff" }) : undefined}
                    // The login is sent again by the server, not drafted: it is
                    // a template and the resend mints a new link, so there is
                    // nothing to edit (#4025 FE-3). Offered while it is out and
                    // unused -- `sent` or `send_failed` -- and not once the
                    // client has signed in, which the server also refuses.
                    onResend={open.message.kind === "login_invite" && (o.login.status === "sent" || o.login.status === "send_failed")
                        ? async () => {
                            const r = await resendOnboardingLogin(o.businessId);
                            if (!r.ok) return r.message;
                            snackbar.showSuccess("The login email was sent again", "The earlier link has stopped working.");
                            onApply(r.onboarding);
                            return null;
                        }
                        : resendDraftOf(open.message)
                            // An access request goes again to the same people, as a new row
                            // in Emails. It is a template, so there are no words to draft:
                            // the server is asked and its refusal is shown.
                            ? async () => {
                                const draft = resendDraftOf(open.message);
                                if (!draft) return "This email has no one to send to.";
                                const r = await sendOnboardingMessage(o.businessId, "access_request", draft);
                                if (!r.ok) return r.message;
                                snackbar.showSuccess("The access request was sent again");
                                onApply(r.onboarding);
                                return null;
                            }
                            : undefined}
                    resendSameEmail={open.message.kind === "access_request"}
                    onClose={onClose}
                />
            )}
            {open?.kind === "release" && (
                <ReleaseDrawer open onboarding={o} onClose={onClose} onSent={onApply} />
            )}
            {open?.kind === "scan" && !FIRST_CHECK_DRAFT_FLOW && (
                <StartScanDrawer open onboarding={o} onClose={onClose} onStarted={onApply} />
            )}
            {open?.kind === "login" && (
                <AddLoginDrawer open businessId={o.businessId} businessName={o.businessName} onClose={onClose} />
            )}
            {open?.kind === "answers" && (
                <AnswersDrawer
                    open
                    onboarding={o}
                    knowledgeHref={href("knowledge")}
                    onStartCheck={steps.some((s) => s.key === "scan" && s.state === "ours") ? openFirstCheck : undefined}
                    onClose={onClose}
                />
            )}
            {open?.kind === "access" && (
                <AccessDrawer key={open.row} open row={open.row} onboarding={o} connectorsHref={href("connectors")} onClose={onClose} onChanged={onApply} />
            )}
        </>
    );
}

function NextCard({
    next, onboarding, onAct, onSecondary, paying, busy, refusal, onPay, onCancelPay, resultsPanel, invites = [], onConfirmInvite,
}: {
    /** Invites the client says are done, shown as a second line when the card leads with something else. */
    invites?: AccessRowKey[];
    onConfirmInvite?: (row: AccessRowKey) => void;
    /** Results and plan: the three parts and the one send, under the card's words. */
    resultsPanel?: ReactNode;
    next: NextAction;
    onboarding: Onboarding;
    onAct: () => void;
    onSecondary?: () => void;
    paying: boolean;
    busy: boolean;
    refusal: string | null;
    onPay: (paidOn: string) => void;
    onCancelPay: () => void;
}) {
    const who = WHO[next.who];
    const tag = next.tag ?? { label: next.waitingOn ? `Needs ${callName(next.waitingOn)}` : who.label, tone: who.tone };
    const [paidOn, setPaidOn] = useState(todayIso);
    const dateId = "record-payment-date";
    // The button that opened the row is gone once it opens: focus goes to the date it asks for.
    const dateRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (paying) dateRef.current?.focus();
    }, [paying]);
    return (
        <NextCardFrame
            complete={next.kind === "done"}
            tag={tag}
            title={next.title}
            recipients={next.kind === "release" ? releaseLine(onboarding, next) : ""}
            why={next.why}
            percent={onboarding.scan.status === "running" ? onboarding.scan.percent : null}
            progressLabel="First AI check"
            action={next.cta && !paying ? (
                <div className="flex shrink-0 flex-wrap items-center gap-2 max-sm:w-full">
                    {next.secondary && onSecondary && (
                        <PillButton variant="text" onClick={onSecondary} disabled={busy} className="max-sm:h-9">{next.secondary}</PillButton>
                    )}
                    <PillButton variant={next.primary ? "primary" : "secondary"} onClick={onAct} busy={busy} className="max-sm:h-9">
                        {next.cta}
                    </PillButton>
                </div>
            ) : null}
        >
            {paying && (
                <form
                    className="mt-4 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-4"
                    onSubmit={(e) => { e.preventDefault(); if (paidOn && paidOn <= todayIso()) onPay(paidOn); }}
                >
                    {/* A date is ten characters: the field keeps that width, not the card's. */}
                    <label htmlFor={dateId} className="min-w-0 flex-1 basis-48 sm:max-w-[220px]">
                        <span className={cn(TYPE.lab, "mb-1.5 block")}>Paid on</span>
                        <input ref={dateRef} id={dateId} type="date" max={todayIso()} required value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className={inputClass(null)} />
                    </label>
                    <div className="ml-auto flex items-center gap-2">
                        <PillButton variant="text" onClick={onCancelPay} disabled={busy} className="max-sm:h-9">Cancel</PillButton>
                        <PillButton type="submit" variant="primary" busy={busy} disabled={!paidOn || paidOn > todayIso()} className="max-sm:h-9">Record the payment</PillButton>
                    </div>
                </form>
            )}
            {resultsPanel}
            {invites.length > 0 && onConfirmInvite && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
                    <p className={cn(TYPE.tb, "m-0 text-gray-700")}>
                        {invites.length === 1 ? "1 invite to confirm" : `${invites.length} invites to confirm`}
                        <span className="text-gray-500"> · {callName(onboarding.contact.name)} says done</span>
                    </p>
                    <PillButton size="sm" variant="text" onClick={() => onConfirmInvite(invites[0])}>
                        {invites.length === 1 ? "Confirm it" : "Confirm them"}
                    </PillButton>
                </div>
            )}
            {refusal && <p role="alert" className={cn(TYPE.tc, "m-0 mt-3 font-medium text-rose-600")}>{refusal}</p>}
        </NextCardFrame>
    );
}

/**
 * Sample data only. Stands in for the client and for Oura, so every state of
 * this screen can be reached with no backend. Never rendered on live data.
 */
function SamplePanel({ onboarding, next, onChanged }: { onboarding: Onboarding; next: NextAction; onChanged: () => void }) {
    const b = onboarding.businessId;
    const name = onboarding.businessName;
    /** Why a move cannot happen now, said when it is pressed rather than a button that does nothing (Angel, 15 Oct). */
    const checkWhy =
        onboarding.buyMode === "online" && onboarding.scan.status === "not_started"
            ? `${name} sells online: its first AI check waits for brand checks, so no check is running to finish.`
            : onboarding.scan.status === "complete"
              ? "The first AI check has already finished."
              : onboarding.scan.status === "failed"
                ? "The first AI check failed. Run it again first."
                : "No AI check is running. Start the first AI check first.";
    const moves = useMemo(() => [
        { label: "Client answers the form", can: !onboarding.form.answeredAt && !!lastSent(onboarding, "kickoff"), why: onboarding.form.answeredAt ? "The form is already answered." : "Send the kick-off first: the form link is in it.", run: () => onboardingSim.formAnswered(b) },
        { label: "AI check finishes", can: onboarding.scan.status === "running", why: checkWhy, run: () => onboardingSim.scanFinished(b) },
        { label: "Client signs in", can: onboarding.login.status === "sent", why: onboarding.login.status === "signed_in" ? "The client has already signed in." : "No login has been sent yet.", run: () => onboardingSim.signedIn(b) },
        { label: "Client gives access", can: onboarding.login.status !== "not_sent" && onboarding.access.some((a) => a.status === "not_started"), why: onboarding.login.status === "not_sent" ? "The login and the access request have not been sent yet." : "Every access step has started already.", run: () => onboardingSim.accessGiven(b) },
        // Uploading on the card (or publishing on the Timeline) does this too; here for a quick walk.
        {
            label: "Timeline uploaded",
            can: next.kind === "publish" || (next.kind === "send_results" && !!onboarding.resultsApprovedAt && !onboarding.resultsPack?.timelineAt),
            why: next.kind === "send_results"
                ? onboarding.resultsPack?.timelineAt ? "The timeline is already uploaded." : "The timeline is uploaded once the results are approved."
                : "The timeline is not due yet: it follows the baseline, the keywords and the signature.",
            run: () => (next.kind === "send_results" ? onboardingSim.timelineUploaded(b) : onboardingSim.published(b)),
        },
    ], [onboarding, b, next.kind, checkWhy]);
    const [why, setWhy] = useState<string | null>(null);
    return (
        <FoldCard id="sample" title="Sample data" icon={{ icon: BeakerIcon, area: "setup" }} foldedFact="Preview only">
            <p className={cn(TYPE.tc, "m-0 mb-3 text-gray-500")}>Preview only. These stand in for the client and for Oura.</p>
            <div className="flex flex-wrap gap-2">
                {moves.map((m) => (
                    <PillButton
                        key={m.label}
                        size="sm"
                        aria-disabled={!m.can || undefined}
                        className={cn(!m.can && "opacity-50")}
                        onClick={() => {
                            if (!m.can) { setWhy(m.why); return; }
                            setWhy(null);
                            m.run();
                            onChanged();
                        }}
                    >
                        {m.label}
                    </PillButton>
                ))}
                <PillButton size="sm" variant="text" onClick={() => { setWhy(null); onboardingSim.reset(); onChanged(); }}>Reset the samples</PillButton>
            </div>
            {why && <p role="status" className={cn(TYPE.tc, "m-0 mt-3 text-gray-600")}>{why}</p>}
        </FoldCard>
    );
}

/**
 * Results and plan (Jack, 19 Oct 2026): the baseline report and the execution
 * timeline go to the client together, once the first AI check is done. Three
 * parts, each with its own button, then one send. The send waits for all
 * three so the client never gets a plan whose dates the schedule does not keep.
 */
/** A part's mark: green tick done, violet dot while Oura works, blue dot when it is ours. */
function PartMark({ state }: { state: "done" | "oura" | "ours" }) {
    if (state === "done") {
        return (
            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                <CheckIcon size={12} strokeWidth={3} />
            </span>
        );
    }
    return (
        <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", state === "oura" ? "border-violet-500" : "border-blue-600")}>
            <span className={cn("h-2 w-2 rounded-full", state === "oura" ? "bg-violet-500 motion-safe:animate-pulse" : "bg-blue-600")} />
        </span>
    );
}

/** One part of the results send: its mark, what it is, and its one control. */
function Part({ mark, title, sub, done, warn = false, children }: { mark: "done" | "oura" | "ours" | "later"; title: string; sub: string; done: boolean; warn?: boolean; children?: ReactNode }) {
    return (
        <li className="flex min-h-12 flex-wrap items-center gap-3 py-2">
            <span aria-hidden className="flex w-5 shrink-0 justify-center">
                {mark === "later" ? <span className="h-5 w-5 rounded-full border-2 border-gray-300 bg-white" /> : <PartMark state={mark} />}
            </span>
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block", mark === "later" ? "text-gray-500" : "text-gray-900")}>
                    {title}
                    <span className="sr-only">{done ? ", done" : ", to do"}</span>
                </span>
                <span className={cn(TYPE.tm, "block", warn ? "font-medium text-amber-700" : "text-gray-500")}>{sub}</span>
            </span>
            {children}
        </li>
    );
}

/** The Monday after a date-only value; a Monday waits for the next one, as the timeline engine does. Null when there is none. */
function mondayAfter(value: string | null | undefined): string | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Results and plan (Jack, 20 Oct 2026), in order: approve the first check's
 * results, Oura makes the Baseline Snapshot from them, upload the execution
 * timeline (it publishes Oura's own schedule, which reads nothing from the
 * PDF), then one send. Each part opens
 * once the one before it is done, so the order is the page's, not memory's.
 */
/** The Results and plan card shown beside an earlier Next step (#4526); its step row scrolls here. */
const RESULTS_CARD_ID = "results-and-plan";

function ResultsPanel({
    onboarding,
    sending,
    unrecorded,
    onUpload,
    onRetry,
    onOpenResults,
    onOpenTimeline,
    onSend,
    onAddKeywords,
}: {
    onboarding: Onboarding;
    sending: boolean;
    unrecorded: boolean;
    onUpload: () => void;
    onRetry: () => void;
    onOpenResults: () => void;
    onOpenTimeline: () => void;
    onSend: () => void;
    onAddKeywords: () => void;
}) {
    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const approvedAt = onboarding.resultsApprovedAt ?? null;
    // The timeline publishes the schedule, which the backend refuses with
    // fewer than 5 priority keywords on the contract (#5256). Read them so the
    // row can say so before the upload (#4487). Null: not read, or the read
    // failed. Unknown blocks nothing.
    const [keywordCount, setKeywordCount] = useState<number | null>(null);
    useEffect(() => {
        let live = true;
        setKeywordCount(null);
        getEngagementIfExists(onboarding.businessId).then(
            (e) => { if (live && e) setKeywordCount(keywordsOf(e).length); },
            () => { /* unknown: the row stays as it is */ },
        );
        return () => { live = false; };
    }, [onboarding.businessId]);
    const timelineAt = onboarding.resultsPack?.timelineAt ?? null;
    // The upload publishes Oura's schedule from the plan's start date; nothing
    // is read from the PDF (backend `mark_execution_timeline`). Work begins the
    // Monday after that date (`next_monday_after` in the timeline engine), so
    // the row names that Monday: the day the PDF's work start has to match.
    const planStart = dayDate(onboarding.plan?.startedAt);
    const workStart = dayDate(mondayAfter(onboarding.plan?.startedAt));
    // Where the start date is changed: Delivery's Plan view, or Contract when Delivery is off.
    const startDateAt = DELIVERY_ON ? "in Delivery, under Plan" : "on the Contract tab";
    // Live, the snapshot is the one Oura makes on the server for this scan (#4423): its row reads it there.
    const assessmentId = onboarding.scan.assessmentId;
    const liveRow = isOnboardingLive && Boolean(approvedAt && assessmentId);
    const [liveReadyAt, setLiveReadyAt] = useState<string | null>(null);
    const snapshotAt = onboarding.snapshotReadyAt ?? liveReadyAt;
    const contact = callName(onboarding.contact.name) || "the client";
    // An upload that is not recorded yet is not in: Send waits for Retry, even over an older timeline.
    const ready = Boolean(approvedAt && snapshotAt && timelineAt && !unrecorded);
    // Only before an upload, once approved, and only when the count is known.
    // Read from the Timeline, the keywords come with it, so they never hold the upload.
    const keywordsShort = Boolean(!TIMELINE_READ_ON && approvedAt && !timelineAt && !unrecorded && keywordCount !== null && keywordCount < KEYWORDS_MIN);
    const timelineVersion = onboarding.resultsPack?.timelineVersion ?? null;
    // A test of the same email to our own inbox, before the real one (#6265). Changes nothing on the record.
    const testTo = onboarding.resultsTestTo ?? null;
    const [testing, setTesting] = useState(false);
    const [testNote, setTestNote] = useState<{ ok: boolean; text: string } | null>(null);
    const sendTest = async () => {
        if (!testTo || testing) return;
        setTesting(true);
        setTestNote(null);
        const r = await sendResultsTest(onboarding.businessId);
        setTesting(false);
        setConfirming(null);
        setTestNote(r.ok ? { ok: true, text: `Test sent to ${testTo}.` } : { ok: false, text: r.message });
    };
    // Every send on this card asks first (#4477): the real one cannot be taken back.
    const [confirming, setConfirming] = useState<"send" | "test" | null>(null);
    const business = onboarding.businessName || onboarding.domain;
    return (
        <div className="mt-4 border-t border-gray-100 pt-3">
            <ol className="list-plain m-0 divide-y divide-gray-100 p-0">
                <Part
                    mark={approvedAt ? "done" : "ours"}
                    title="First AI check results"
                    sub={approvedAt ? `Approved ${dayMonth(approvedAt)}. The client sees nothing until you send.` : "Approve results sits on the answers page, above the answers."}
                    done={Boolean(approvedAt)}
                >
                    <div className="flex shrink-0 items-center gap-2">
                        {approvedAt ? (
                            <PillButton size="sm" onClick={onOpenResults}>Open the answers</PillButton>
                        ) : (
                            // Approval lives on the answers page, beside what is being approved.
                            <PillButton size="sm" variant="primary" onClick={onOpenResults}>Review the answers</PillButton>
                        )}
                    </div>
                </Part>
                {liveRow && assessmentId ? (
                    // Keyed by the scan: an open drawer or a make in flight never carries over to another one.
                    <BaselineSnapshotRow
                        key={assessmentId}
                        assessmentId={assessmentId}
                        businessName={onboarding.businessName || onboarding.domain}
                        onReady={setLiveReadyAt}
                    />
                ) : (
                    <Part
                        mark={snapshotAt ? "done" : approvedAt ? "oura" : "later"}
                        title="Baseline Snapshot"
                        sub={snapshotAt ? `Made by Oura from the approved results, ${dayMonth(snapshotAt)}.` : approvedAt ? "Oura is making it from the approved results. It usually takes a few minutes; you can upload the timeline meanwhile." : "Oura makes it once the results are approved."}
                        done={Boolean(snapshotAt)}
                    >
                        {snapshotAt ? (
                            <PillButton size="sm" onClick={() => setSnapshotOpen(true)}>Open</PillButton>
                        ) : approvedAt ? (
                            <span role="status" className={cn(TYPE.tc, "inline-flex shrink-0 items-center gap-1.5 text-violet-700")}>
                                <Loader2Icon aria-hidden size={13} className="motion-safe:animate-spin" />
                                Making it
                            </span>
                        ) : null}
                    </Part>
                )}
                <Part
                    mark={timelineAt ? "done" : approvedAt ? "ours" : "later"}
                    title="Execution timeline"
                    sub={TIMELINE_READ_ON
                        ? timelineAt
                            ? `Uploaded ${dayMonth(timelineAt)}. Oura's plan follows Timeline ${timelineVersion ?? "as uploaded"}: dates, answers and work.`
                            : "Upload the PDF and its data file. Oura checks them against the quote and shows what changes before anything is saved."
                        : timelineAt
                        ? `Uploaded ${dayMonth(timelineAt)}. ${workStart ? `Oura starts work ${workStart}` : "Oura's dates come from the plan's start date"}, not from the PDF. If the timeline starts work another day, move the start date ${startDateAt}.`
                        : keywordsShort
                          ? `Add the priority keywords first: ${keywordCount} of the 5 to 10 agreed at the quotation ${keywordCount === 1 ? "is" : "are"} recorded. The timeline publishes the schedule, which needs them.`
                          : planStart && workStart
                            ? `Oura starts work ${workStart}, the Monday after the start date (${planStart}), and does not read the PDF. If the timeline starts work another day, first move the start date ${startDateAt}.`
                            : `Oura's schedule does not read the PDF. Work starts the Monday after the start date, so set it ${startDateAt} first, then upload.`}
                    done={Boolean(timelineAt)}
                    warn={keywordsShort}
                >
                    {unrecorded ? (
                        // Uploaded but not added to the results: Retry adds it, rather than a second upload.
                        <PillButton size="sm" variant="primary" onClick={onRetry}>Retry</PillButton>
                    ) : timelineAt ? (
                        <div className="flex shrink-0 items-center gap-2">
                            <PillButton size="sm" variant="text" onClick={onUpload}>Replace</PillButton>
                            <PillButton size="sm" onClick={onOpenTimeline}>See the schedule</PillButton>
                        </div>
                    ) : keywordsShort ? (
                        <PillButton size="sm" variant="primary" onClick={onAddKeywords}>Add priority keywords</PillButton>
                    ) : (
                        approvedAt && <PillButton size="sm" variant="primary" onClick={onUpload}>Upload</PillButton>
                    )}
                </Part>
            </ol>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    {ready
                        ? `One email from ${callName(onboarding.accountManager.name) || "us"} with the snapshot and the timeline. ${contact} sees the results when it goes.`
                        : approvedAt && !snapshotAt
                          ? timelineAt ? "Send opens once the snapshot is ready." : "Send opens once the snapshot is ready and the timeline is uploaded."
                          : approvedAt
                            ? "Send opens once the timeline is uploaded."
                            : "Send opens once all three are done."}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    {testTo && (
                        <PillButton variant="text" busy={testing} disabled={!ready || sending} onClick={() => setConfirming("test")} className="max-sm:h-9">
                            Send Internal Test
                        </PillButton>
                    )}
                    <PillButton variant="primary" busy={sending} disabled={!ready} onClick={() => setConfirming("send")} className="max-sm:h-9">
                        Send to {contact}
                    </PillButton>
                </div>
            </div>
            <Dialog open={confirming !== null} onOpenChange={(open) => { if (!open && !testing) setConfirming(null); }}>
                <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl">
                    <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">
                        {confirming === "test" ? `Send a test to ${testTo}?` : `Send ${business}'s results and plan to ${contact}?`}
                    </DialogTitle>
                    <DialogDescription asChild>
                        <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                            {confirming === "test" ? (
                                <>
                                    <p className="m-0">The same email {contact} would get goes to {testTo} only.</p>
                                    <p className="m-0 text-gray-500">Nothing changes for {contact}: the results stay unsent.</p>
                                </>
                            ) : (
                                <>
                                    <p className="m-0">{contact} gets one email with the Baseline Snapshot and the execution timeline, and sees the results in Oura from now on.</p>
                                    <p className="m-0 text-gray-500">This cannot be taken back.{testTo ? ` To check the email first, send a test to ${testTo}.` : ""}</p>
                                </>
                            )}
                        </div>
                    </DialogDescription>
                    <div className="mt-5 flex justify-end gap-2">
                        <PillButton size="sm" disabled={testing} onClick={() => setConfirming(null)}>Cancel</PillButton>
                        {confirming === "test" ? (
                            <PillButton size="sm" variant="primary" busy={testing} onClick={() => void sendTest()}>
                                {testing ? "Sending…" : "Send the test"}
                            </PillButton>
                        ) : (
                            <PillButton size="sm" variant="primary" onClick={() => { setConfirming(null); onSend(); }}>
                                Send to {contact}
                            </PillButton>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            {testNote && (
                <p role={testNote.ok ? "status" : "alert"} className={cn(TYPE.tc, "m-0 mt-2 text-right", testNote.ok ? "text-gray-500" : "font-medium text-rose-600")}>
                    {testNote.text}
                </p>
            )}
            <SnapshotDrawer
                open={snapshotOpen && !liveRow}
                onClose={() => setSnapshotOpen(false)}
                businessName={onboarding.businessName || onboarding.domain}
                readyAt={snapshotAt}
                url={onboarding.snapshotUrl ?? null}
            />
        </div>
    );
}

/**
 * The Access card's count, said so it agrees with what the client sees. The
 * client counts a step done once THEY have done it; staff count it once we
 * hold it, so the same day read "3 of 4" there and "2 of 4" here (audit,
 * 24 Oct 2026). The gap is the access they gave that we have not checked, so
 * it is said: "2 of 4 held, 1 to check".
 */
function accessFact(asked: ReturnType<typeof accessAsked>): string {
    const held = asked.filter(isHeld).length;
    const toCheck = asked.filter((a) => a.status === "claimed").length;
    return toCheck > 0 ? `${held} of ${asked.length} held, ${toCheck} to check` : `${held} of ${asked.length}`;
}
